import { RiCheckLine, RiCloseLine, RiInformationLine } from '@remixicon/react';
import { Badge } from '@backstage/ui';
import type { ReactNode } from 'react';

import styles from './StatusBadge.module.css';

export type ChipColor = 'success' | 'danger' | 'neutral';

const ICON_SIZE = 12;

const TONE_ICONS: Partial<Record<ChipColor, ReactNode>> = {
  success: <RiCheckLine size={ICON_SIZE} />,
  danger: <RiCloseLine size={ICON_SIZE} />,
  neutral: <RiInformationLine size={ICON_SIZE} />,
};

export interface StatusBadgeProps {
  /** Color of the badge. Callers map their own domain values to a tone. */
  tone?: ChipColor;
  size?: 'small' | 'medium';
  /**
   * Icon to show before the label.
   * - Omit (undefined): use the default icon for the tone.
   * - null: explicitly no icon.
   * - ReactNode: override with a custom icon.
   */
  icon?: ReactNode | null;
  /** The visible label. */
  children: ReactNode;
  className?: string;
  title?: string;
  role?: string;
  'aria-label'?: string;
}

export function StatusBadge({
  tone = 'neutral',
  size = 'medium',
  icon,
  children,
  className,
  ...rest
}: StatusBadgeProps) {
  const resolvedIcon = icon === null ? undefined : icon ?? TONE_ICONS[tone];
  return (
    <Badge
      size={size}
      icon={resolvedIcon}
      className={[styles.badge, styles[tone], className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {children}
    </Badge>
  );
}
